import React, { useEffect, useState } from "react";
import { Button, Table, Dropdown } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { AiOutlineDelete, AiOutlineEdit } from "react-icons/ai";
import { BiSearch, BiPencil, BiDotsHorizontalRounded } from "react-icons/bi";
import { FcFinePrint } from "react-icons/fc";
import { toast } from "react-toastify";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import Pagination from "../../../components/Pagination/Pagination";
import { DEFAULT_ITEMS_PER_PAGE } from "../../../core/constants/common";
import DashGraphConfigService from "../../../core/service/DashGraphConfigService";
import AssignSetToCompanyModal from "./AssignSetToCompanyModal";
import EditGraphSetModal from "./EditGraphSetModal";
import "./GraphConfigList.scss";

const GraphSetList = ({ t }) => {
  const [itemsTotalCount, setItemTotalCount] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageData, setPageData] = useState([]);
  const [setToAssign, setSetToAssign] = useState(null);
  const [setToDelete, setSetToDelete] = useState(null);
  const [unassignTarget, setUnassignTarget] = useState(null);
  const [setToEdit, setSetToEdit] = useState(null);
  const [searchKey, setSearchKey] = useState("");

  useEffect(() => {
    listSets();
  }, []);

  const listSets = (pageNumber, itemsPerPageVal, searchKeyValue = null) => {
    if (pageNumber > 0) {
      pageNumber = pageNumber - 1;
    } else {
      setCurrentPage(1);
    }
    const perPage = itemsPerPageVal > 0 ? itemsPerPageVal : itemsPerPage;
    const searchValue = searchKeyValue !== null ? searchKeyValue : searchKey;

    DashGraphConfigService.getGraphSets(pageNumber, perPage, searchValue.trim())
      .then((response) => {
        if (response.status !== 200) return;

        const list = Array.isArray(response.data) ? response.data : [];
        setItemTotalCount(parseFloat(response.headers?.["x-total-count"]) || list.length);
        setPageData(list);
      })
      .catch(() => { });
  };

  const handleUnassignConfirm = (confirmed) => {
    const target = unassignTarget;
    setUnassignTarget(null);
    if (!confirmed || !target) return;

    DashGraphConfigService.unassignSetFromCompany(target.setId, target.companyId)
      .then((res) => {
        if (res.status !== 200 && res.status !== 201 && res.status !== 204) return;

        toast.success("Success", { position: toast.POSITION.TOP_RIGHT });
        listSets(currentPage, itemsPerPage);
      })
      .catch(() => { });
  };

  const handleDeleteConfirm = (confirmed, setId) => {
    setSetToDelete(null);
    if (!confirmed) return;

    DashGraphConfigService.deleteGraphSet(setId)
      .then((res) => {
        if (res.status !== 200 && res.status !== 201 && res.status !== 204) return;

        toast.success("Success", { position: toast.POSITION.TOP_RIGHT });
        // step back a page if the last item on a non-first page was deleted
        const page = pageData.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
        setCurrentPage(page);
        listSets(page, itemsPerPage);
      })
      .catch(() => { });
  };

  const handleSearch = () => {
    setCurrentPage(1);
    listSets(1, itemsPerPage, searchKey);
  };

  const clearSearch = () => {
    setSearchKey("");
    setCurrentPage(1);
    listSets(1, itemsPerPage, "");
  };

  return (
    <>
      <div className="FormsList">
        <div className="List_header mb-1">
          <h5 className="MH">{t("GraphSets.label")}</h5>
          <div className="search-section d-flex justify-content-end align-items-center">
            <div className="searchListBox">
              <span><BiSearch /></span>
              <input
                type="text"
                className="form-control"
                placeholder={t("SearchSet.label")}
                value={searchKey}
                onChange={(e) => setSearchKey(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>
            {searchKey && (
              <Button
                variant="outline-secondary"
                onClick={clearSearch}
                className="ms-2"
                size="sm">
                {t("Clear.label")}
              </Button>
            )}
          </div>
        </div>

        <div className="List_Table_01">
          <div className="card List_table-card01">
            <Table responsive="sm">
              <thead>
                <tr>
                  <th>{t("SetName.label")}</th>
                  <th>{t("Widgets.label")}</th>
                  <th>{t("AssignedCompanies.label")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pageData?.length > 0 ? (
                  pageData.map((graphSet) => (
                    <tr key={graphSet.id}>
                      <td>
                        {graphSet.name}

                      </td>
                      <td>
                        <div className="GraphSetWidgetBox">
                          {graphSet.graphs?.map((graph) => (
                            <span key={graph.id}>
                              {graph.title}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        <div className="GraphSetWidgetBox">
                          {graphSet.assignedCompanies?.map((company) => (
                            <span key={company.id}>
                              {company.name}
                              {/* <Button
                                variant="outline-danger"
                                size="sm"
                                className="ms-1"
                                onClick={() => setUnassignTarget({ setId: graphSet.id, companyId: company.id })}>
                                {t("Unassign.label")}
                              </Button> */}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="text-end">
                        <Dropdown>
                          <Dropdown.Toggle
                            variant="blank_outline"
                            id="graphConfigListDropdown">
                            <BiDotsHorizontalRounded />
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => setSetToAssign(graphSet)}>
                              {t("AssignCompany.label")}
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => setSetToEdit(graphSet)}>
                              {t("Edit.label")}
                            </Dropdown.Item>
                            {!graphSet.assignedCompanies?.length && (
                              <Dropdown.Item onClick={() => setSetToDelete(graphSet)}>{t("Delete.label")}</Dropdown.Item>
                            )}
                          </Dropdown.Menu>
                        </Dropdown>

                        {/* <Button
                          variant="link"
                          size="sm"
                          className="ms-1 p-0"
                          title={t("Edit.label")}
                          onClick={() => setSetToEdit(graphSet)}>
                          <BiPencil size={16} />
                        </Button>
                        {!graphSet.assignedCompanies?.length && (
                          <Button
                            variant="link"
                            size="sm"
                            className="text-danger ms-2"
                            title={t("Delete.label")}
                            onClick={() => setSetToDelete(graphSet)}>
                            <AiOutlineDelete size={18} />
                          </Button>
                        )} */}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center">
                      <div className="Nodatafound_box">
                        <h6><FcFinePrint className="me-2" /> {t("Nodatafound.label")}</h6>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
            <div className="p-2 List_footer_count">
              <Pagination
                itemsTotalCount={itemsTotalCount}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                alwaysShown={true}
                setItemsPerPage={setItemsPerPage}
                pageClicked={(pageNum, perPage) => {
                  listSets(pageNum, perPage);
                }} />
            </div>
          </div>
        </div>
      </div>
      {unassignTarget && (
        <ConfirmationModal
          modalShow={true}
          title={t("Unassign.label")}
          messageText={t("ConfirmUnassignSet.label")}
          confirmBtn={t("Unassign.label")}
          callbackModal={handleUnassignConfirm}
        />
      )}
      {setToDelete && (
        <ConfirmationModal
          modalShow={true}
          id={setToDelete.id}
          title={t("Delete.label")}
          messageText={t("ConfirmDeleteSet.label")}
          confirmBtn={t("Delete.label")}
          callbackModal={handleDeleteConfirm}
        />
      )}
      <EditGraphSetModal
        show={!!setToEdit}
        graphSet={setToEdit}
        onClose={() => setSetToEdit(null)}
        onSaved={() => listSets(currentPage, itemsPerPage)}
      />
      <AssignSetToCompanyModal
        show={!!setToAssign}
        graphSet={setToAssign}
        onClose={() => setSetToAssign(null)}
        onSaved={() => listSets(currentPage, itemsPerPage)}
      />
    </>
  );
};

export default withTranslation()(GraphSetList);
